/**
 * Copal 22 — GA4 Enhanced Ecommerce Event Tracking
 * 
 * Hooks into Whisk theme's pub/sub system + native DOM events
 * to fire GA4 ecommerce events through GTM's dataLayer.
 * 
 * Events tracked:
 *   - add_to_cart       (product form submit success)
 *   - remove_from_cart  (cart item removal)
 *   - begin_checkout    (checkout button click)
 *   - view_item_list    (product visible in viewport)
 *   - select_item       (product card click)
 *   - view_cart         (cart drawer open)
 *   - search            (search query submitted)
 */

(function() {
  'use strict';

  // Safety: only run if dataLayer and PUB_SUB_EVENTS exist
  if (typeof dataLayer === 'undefined') return;

  const dl = window.dataLayer;

  // ─── HELPERS ──────────────────────────────────────────────────────────────

  function clearAndPush(obj) {
    dl.push({ ecommerce: null }); // GA4 best practice: clear previous ecommerce object
    dl.push(obj);
  }

  function getProductDataFromVariantId(variantId) {
    // Attempt to read product JSON embedded in page (Shopify standard)
    const productEl = document.getElementById('product-json');
    if (!productEl) return null;
    try {
      const product = JSON.parse(productEl.textContent);
      const variant = product.variants.find(v => String(v.id) === String(variantId)) || product.variants[0];
      return {
        item_id: String(product.id),
        item_name: product.title,
        item_brand: 'Copal 22',
        item_category: 'Tequila',
        item_category2: 'Reposado',
        item_variant: variant ? variant.title : '',
        price: variant ? (variant.price / 100) : 0,
        quantity: 1
      };
    } catch(e) {
      return null;
    }
  }

  function getQuantityForVariant(variantId) {
    // Look for quantity input in the product form
    const qtyInput = document.querySelector('input[name="quantity"]');
    return qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
  }

  // ─── ADD TO CART ──────────────────────────────────────────────────────────
  // Hooks into the theme's PUB_SUB_EVENTS.cartUpdate which fires after
  // a successful add-to-cart fetch in product-form.js

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function(data) {
      if (data.source !== 'product-form') return;

      const variantId = data.productVariantId;
      const itemData = getProductDataFromVariantId(variantId);
      if (!itemData) return;

      itemData.quantity = getQuantityForVariant(variantId);

      clearAndPush({
        event: 'add_to_cart',
        ecommerce: {
          currency: 'USD',
          value: itemData.price * itemData.quantity,
          items: [itemData]
        }
      });
    });
  }

  // ─── REMOVE FROM CART ─────────────────────────────────────────────────────
  // Listens for clicks on remove/decrease buttons in the cart drawer

  document.addEventListener('click', function(e) {
    // Cart remove button
    const removeBtn = e.target.closest('[data-quantity-variant-id]');
    if (!removeBtn) return;

    const isRemove = e.target.closest('[data-increase-quantity="false"]') || 
                     e.target.closest('.cart-item__remove');
    if (!isRemove) return;

    const variantId = removeBtn.dataset.quantityVariantId;
    const cartItem = removeBtn.closest('.cart-item, [class*="cart-item"]');
    if (!cartItem) return;

    const priceEl = cartItem.querySelector('[data-cart-item-price]');
    const nameEl = cartItem.querySelector('.cart-item__name, [class*="cart-item__name"]');
    const qtyEl = cartItem.querySelector('input[name="updates[]"]');

    const price = priceEl ? parseFloat(priceEl.dataset.price || 0) / 100 : 0;
    const name = nameEl ? nameEl.textContent.trim() : 'Copal Reposado';
    const qty = qtyEl ? parseInt(qtyEl.value, 10) || 1 : 1;

    clearAndPush({
      event: 'remove_from_cart',
      ecommerce: {
        currency: 'USD',
        value: price,
        items: [{
          item_id: variantId,
          item_name: name,
          item_brand: 'Copal 22',
          item_category: 'Tequila',
          item_category2: 'Reposado',
          price: price,
          quantity: qty
        }]
      }
    });
  });

  // ─── BEGIN CHECKOUT ───────────────────────────────────────────────────────
  // Fires when any checkout button is clicked

  document.addEventListener('click', function(e) {
    const checkoutBtn = e.target.closest('[name="checkout"], [href*="/checkout"], #checkout, .checkout-button, [id*="checkout"]');
    if (!checkoutBtn) return;

    // Try to get cart data from the page
    fetch('/cart.js')
      .then(r => r.json())
      .then(function(cart) {
        const items = (cart.items || []).map(function(item) {
          return {
            item_id: String(item.variant_id),
            item_name: item.product_title,
            item_brand: 'Copal 22',
            item_category: 'Tequila',
            item_category2: 'Reposado',
            item_variant: item.variant_title,
            price: item.price / 100,
            quantity: item.quantity
          };
        });

        clearAndPush({
          event: 'begin_checkout',
          ecommerce: {
            currency: 'USD',
            value: cart.total_price / 100,
            coupon: (cart.cart_level_discount_applications || []).map(d => d.title).join(','),
            items: items
          }
        });
      })
      .catch(function() {});
  });

  // ─── VIEW CART ────────────────────────────────────────────────────────────
  // Fires when cart drawer opens

  document.addEventListener('click', function(e) {
    const cartOpener = e.target.closest('#cart-drawer-opener, [aria-controls="cart-drawer"], [href="/cart"]');
    if (!cartOpener) return;

    fetch('/cart.js')
      .then(r => r.json())
      .then(function(cart) {
        if (!cart.item_count) return; // Don't fire for empty cart

        const items = (cart.items || []).map(function(item) {
          return {
            item_id: String(item.variant_id),
            item_name: item.product_title,
            item_brand: 'Copal 22',
            item_category: 'Tequila',
            item_category2: 'Reposado',
            item_variant: item.variant_title,
            price: item.price / 100,
            quantity: item.quantity
          };
        });

        clearAndPush({
          event: 'view_cart',
          ecommerce: {
            currency: 'USD',
            value: cart.total_price / 100,
            items: items
          }
        });
      })
      .catch(function() {});
  });

  // ─── VARIANT CHANGE ───────────────────────────────────────────────────────
  // Fires when user switches between bottle count variants

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, function(data) {
      if (!data || !data.data || !data.data.variant) return;
      const variant = data.data.variant;

      dl.push({
        event: 'select_item',
        ecommerce: {
          item_list_name: 'Variant Selector',
          items: [{
            item_id: String(variant.id),
            item_name: variant.name || 'Copal Reposado',
            item_brand: 'Copal 22',
            item_category: 'Tequila',
            item_category2: 'Reposado',
            item_variant: variant.title,
            price: variant.price / 100,
            quantity: 1
          }]
        }
      });
    });
  }

  // ─── SCROLL DEPTH ─────────────────────────────────────────────────────────
  // Fires at 25%, 50%, 75%, 90% scroll — useful for content engagement scoring

  (function() {
    const milestones = [25, 50, 75, 90];
    const fired = new Set();

    function getScrollDepth() {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      return docHeight > 0 ? Math.round((scrollTop / docHeight) * 100) : 0;
    }

    let ticking = false;
    window.addEventListener('scroll', function() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function() {
        const depth = getScrollDepth();
        milestones.forEach(function(m) {
          if (depth >= m && !fired.has(m)) {
            fired.add(m);
            dl.push({
              event: 'scroll_depth',
              scroll_depth_threshold: m,
              page_type: document.body.className.match(/body--template-(\S+)/)?.[1] || 'unknown'
            });
          }
        });
        ticking = false;
      });
    }, { passive: true });
  })();

  // ─── ENGAGEMENT TIME ──────────────────────────────────────────────────────
  // Fires when user has spent meaningful time on product page

  (function() {
    if (!window.location.pathname.includes('/products/')) return;
    let fired30 = false;
    let fired60 = false;

    setTimeout(function() {
      if (!fired30 && !document.hidden) {
        fired30 = true;
        dl.push({ event: 'product_page_engagement', engagement_seconds: 30 });
      }
    }, 30000);

    setTimeout(function() {
      if (!fired60 && !document.hidden) {
        fired60 = true;
        dl.push({ event: 'product_page_engagement', engagement_seconds: 60 });
      }
    }, 60000);
  })();

})();
